// Store del consenso cookie GDPR
import { create } from 'zustand';

export type CookieCategory = 'necessary' | 'analytics' | 'marketing';

export interface CookieConsent {
  necessary: boolean; // sempre true
  analytics: boolean;
  marketing: boolean;
  consentDate: string | null; // ISO
  consentVersion: string; // versione dell'informativa
}

const STORAGE_KEY = 'pira_cookie_consent';
const CONSENT_VERSION = '1.0';

const DEFAULT: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  consentDate: null,
  consentVersion: CONSENT_VERSION,
};

interface CookieState extends CookieConsent {
  hasDecided: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  setPreferences: (p: Partial<Pick<CookieConsent, 'analytics' | 'marketing'>>) => void;
  reset: () => void;
}

function loadFromStorage(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    // Se la versione è cambiata, richiedi nuovo consenso
    if (parsed.consentVersion !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveToStorage(c: CookieConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch { /* ignore */ }
}

export const useCookieConsentStore = create<CookieState>((set, get) => {
  const saved = loadFromStorage();
  const initial = saved ?? DEFAULT;

  return {
    ...initial,
    hasDecided: !!saved,

    acceptAll: () => {
      const c: CookieConsent = {
        necessary: true,
        analytics: true,
        marketing: true,
        consentDate: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(c);
      set({ ...c, hasDecided: true });
    },

    rejectAll: () => {
      const c: CookieConsent = {
        necessary: true,
        analytics: false,
        marketing: false,
        consentDate: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(c);
      set({ ...c, hasDecided: true });
    },

    setPreferences: (p) => {
      const c: CookieConsent = {
        necessary: true,
        analytics: p.analytics ?? get().analytics,
        marketing: p.marketing ?? get().marketing,
        consentDate: new Date().toISOString(),
        consentVersion: CONSENT_VERSION,
      };
      saveToStorage(c);
      set({ ...c, hasDecided: true });
    },

    reset: () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      set({ ...DEFAULT, hasDecided: false });
    },
  };
});
